import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  SxProps,
  Theme,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { motion } from "framer-motion";
import { tokens } from "../../../theme";

const MotionTableRow = motion(TableRow);

export interface ColumnDef<T> {
  key: string;
  label: string;
  render: (row: T, index: number) => React.ReactNode;
  headerSx?: SxProps<Theme>;
  cellSx?: SxProps<Theme>;
  align?: "left" | "center" | "right";
}

interface AnimatedTableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  emptyMessage?: string;
  minWidth?: number;
}

function AnimatedTable<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  page,
  totalPages,
  onPageChange,
  emptyMessage = "No data found.",
  minWidth = 900,
}: AnimatedTableProps<T>) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const headerCellSx: SxProps<Theme> = {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary,
    backgroundColor: colors.primary[500],
    borderBottom: "none",
    py: 2,
    px: 3,
  };

  const bodyCellSx: SxProps<Theme> = {
    fontSize: 14,
    color: theme.palette.text.secondary,
    borderBottom: "none",
    py: 2,
    px: 3,
  };

  return (
    <>
      <TableContainer sx={{ overflowX: "auto", flexGrow: 1 }}>
        <Table
          sx={{
            minWidth,
            borderCollapse: "separate",
            borderSpacing: "0 4px",
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell
                  key={col.key}
                  align={col.align ?? "left"}
                  sx={{
                    ...headerCellSx,
                    ...(idx === 0 && {
                      borderTopLeftRadius: "16px",
                      borderBottomLeftRadius: "16px",
                    }),
                    ...(idx === columns.length - 1 && {
                      borderTopRightRadius: "16px",
                      borderBottomRightRadius: "16px",
                    }),
                    ...col.headerSx,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ ...bodyCellSx, textAlign: "center", py: 6 }}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <MotionTableRow
                  key={getRowId(row)}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                  onClick={() => onRowClick?.(row)}
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    "&:hover td": {
                      backgroundColor: onRowClick
                        ? theme.palette.action.hover
                        : "inherit",
                    },
                    "&:hover td:first-of-type": {
                      borderTopLeftRadius: "12px",
                      borderBottomLeftRadius: "12px",
                    },
                    "&:hover td:last-child": {
                      borderTopRightRadius: "12px",
                      borderBottomRightRadius: "12px",
                    },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align ?? "left"}
                      sx={{ ...bodyCellSx, ...col.cellSx }}
                    >
                      {col.render(row, idx)}
                    </TableCell>
                  ))}
                </MotionTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mt: "auto",
          pt: 2,
          gap: 1,
        }}
      >
        <IconButton
          size="small"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          sx={{
            border: `1px solid ${colors.primary[300]}`,
            color: theme.palette.text.primary,
            borderRadius: "50%",
            "&.Mui-disabled": {
              borderColor: theme.palette.action.disabled,
              color: theme.palette.action.disabled,
            },
          }}
        >
          <ChevronLeft sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
          sx={{
            border: `1px solid ${colors.primary[300]}`,
            color: theme.palette.text.primary,
            borderRadius: "50%",
            "&.Mui-disabled": {
              borderColor: theme.palette.action.disabled,
              color: theme.palette.action.disabled,
            },
          }}
        >
          <ChevronRight sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    </>
  );
}

export default AnimatedTable;